// frontend/src/components/leads/LeadFilters.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";

const OWNER_ROLES = ["Admin", "SM", "Sales Owner", "Sales Manager"];

function normalizeRole(role) {
  return String(role || "")
    .trim()
    .toUpperCase();
}

function isOwnerRole(role) {
  return OWNER_ROLES.includes(String(role || "").trim());
}

function extractArrayResponse(response, keys = []) {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(response[key])) return response[key];
  }
  return [];
}

function extractProducts(data) {
  return extractArrayResponse(data, ["products", "data", "items"]);
}

function normalizeProduct(product) {
  if (typeof product === "string") {
    return { id: product, name: product, category: "", subcategory: "" };
  }
  return {
    id:
      product?.id ??
      product?.product_id ??
      product?.productId ??
      product?.name ??
      product?.product_name,
    name: product?.name ?? product?.product_name ?? product?.productName ?? "",
    category:
      product?.category_name ??
      product?.categoryName ??
      product?.category ??
      "",
    subcategory:
      product?.subcategory_name ??
      product?.subcategoryName ??
      product?.subcategory ??
      "",
  };
}

const masterDataCache = {
  statuses: null,
  dmsProviders: null,
  leadTypes: null,
  leadSources: null,
  serviceDashboards: null,
  productCatalog: null,
  salesReps: null,
  salesRepsLoaded: false,
};

let masterDataPromise = null;
let salesRepsPromise = null;

async function loadSharedMasterData() {
  if (
    masterDataCache.statuses &&
    masterDataCache.dmsProviders &&
    masterDataCache.leadTypes &&
    masterDataCache.leadSources &&
    masterDataCache.serviceDashboards &&
    masterDataCache.productCatalog
  ) {
    return masterDataCache;
  }

  if (!masterDataPromise) {
    masterDataPromise = Promise.all([
      api.getMasterData("leadStatuses"),
      api.getMasterData("dms"),
      api.getMasterData("leadTypes"),
      api.getMasterData("leadSources"),
      api.getMasterData("serviceDashboards"),
      api.getMasterData("products"),
    ])
      .then(([statuses, dms, types, sources, dashboards, products]) => {
        masterDataCache.statuses = extractArrayResponse(statuses, [
          "statuses",
          "data",
          "items",
        ]);
        masterDataCache.dmsProviders = extractArrayResponse(dms, [
          "dms",
          "providers",
          "data",
          "items",
        ]);
        masterDataCache.leadTypes = extractArrayResponse(types, [
          "leadTypes",
          "data",
          "items",
        ]);
        masterDataCache.leadSources = extractArrayResponse(sources, [
          "leadSources",
          "data",
          "items",
        ]);
        masterDataCache.serviceDashboards = extractArrayResponse(dashboards, [
          "serviceDashboards",
          "data",
          "items",
        ]);
        masterDataCache.productCatalog = extractProducts(products);
        return masterDataCache;
      })
      .finally(() => {
        masterDataPromise = null;
      });
  }

  return masterDataPromise;
}

async function loadSharedSalesReps() {
  if (masterDataCache.salesRepsLoaded) {
    return masterDataCache.salesReps || [];
  }

  if (!salesRepsPromise) {
    salesRepsPromise = api
      .getSalesReps()
      .then((response) => {
        const reps = extractArrayResponse(response, [
          "salesReps",
          "sales_reps",
          "users",
          "data",
          "items",
        ]);
        masterDataCache.salesReps = reps;
        masterDataCache.salesRepsLoaded = true;
        return reps;
      })
      .catch((error) => {
        console.error("Failed to load Sales Reps:", error);
        masterDataCache.salesReps = [];
        masterDataCache.salesRepsLoaded = true;
        return [];
      })
      .finally(() => {
        salesRepsPromise = null;
      });
  }

  return salesRepsPromise;
}

function LeadFilters({ onFilterChange, activeStatus = "all", userRole = "" }) {
  const [statuses, setStatuses] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [serviceDashboards, setServiceDashboards] = useState([]);
  const [dmsProviders, setDmsProviders] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [salesReps, setSalesReps] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [dmsMenuOpen, setDmsMenuOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const productDropdownRef = useRef(null);
  const dmsDropdownRef = useRef(null);

  const [filters, setFilters] = useState({
    status: activeStatus || "all",
    leadType: "all",
    leadSource: "all",
    serviceDashboard: "all",
    search: "",
    category: "",
    subcategory: "",
    dmsProviders: [],
    products: [],
    salesRep: "all",
  });

  const ownerCanFilterSalesRep = isOwnerRole(userRole);

  useEffect(() => {
    loadMasterData();
  }, [userRole]);

  useEffect(() => {
    setFilters((previous) => ({
      ...previous,
      status: activeStatus || "all",
    }));
  }, [activeStatus]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideProduct = productDropdownRef.current?.contains(
        event.target,
      );
      const clickedInsideDms = dmsDropdownRef.current?.contains(event.target);

      if (!clickedInsideProduct && !clickedInsideDms) {
        setProductMenuOpen(false);
        setDmsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const loadMasterData = async () => {
    try {
      setLoadingProducts(true);
      const data = await loadSharedMasterData();
      setStatuses(data.statuses || []);
      setDmsProviders(data.dmsProviders || []);
      setLeadTypes(data.leadTypes || []);
      setLeadSources(data.leadSources || []);
      setServiceDashboards(data.serviceDashboards || []);
      setProductCatalog(data.productCatalog || []);

      if (ownerCanFilterSalesRep) {
        const reps = await loadSharedSalesReps();
        setSalesReps(reps);
      } else {
        setSalesReps([]);
      }
    } catch (error) {
      console.error("Failed to load master/filter data:", error);
      setStatuses([]);
      setDmsProviders([]);
      setLeadTypes([]);
      setLeadSources([]);
      setServiceDashboards([]);
      setProductCatalog([]);
      setSalesReps([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const normalizedProducts = useMemo(() => {
    return productCatalog
      .map(normalizeProduct)
      .filter((product) => product.name);
  }, [productCatalog]);

  const categories = useMemo(() => {
    return [
      ...new Set(
        normalizedProducts.map((product) => product.category).filter(Boolean),
      ),
    ].sort();
  }, [normalizedProducts]);

  const subcategories = useMemo(() => {
    return [
      ...new Set(
        normalizedProducts
          .filter((product) => {
            if (!filters.category) return true;
            return product.category === filters.category;
          })
          .map((product) => product.subcategory)
          .filter(Boolean),
      ),
    ].sort();
  }, [normalizedProducts, filters.category]);

  const availableProducts = useMemo(() => {
    return normalizedProducts
      .filter((product) => {
        if (filters.category && product.category !== filters.category)
          return false;
        if (filters.subcategory && product.subcategory !== filters.subcategory)
          return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [normalizedProducts, filters.category, filters.subcategory]);

  const getEffectiveProducts = (nextFilters, explicitProducts) => {
    const selected = Array.isArray(explicitProducts) ? explicitProducts : [];
    if (selected.length > 0) return selected;
    if (!nextFilters.category && !nextFilters.subcategory) return [];
    return normalizedProducts
      .filter((product) => {
        if (nextFilters.category && product.category !== nextFilters.category)
          return false;
        if (
          nextFilters.subcategory &&
          product.subcategory !== nextFilters.subcategory
        )
          return false;
        return true;
      })
      .map((product) => product.name)
      .filter(Boolean);
  };

  const emitFilters = (nextFilters) => {
    setFilters(nextFilters);
    onFilterChange(nextFilters);
  };

  const handleChange = (field, value) => {
    const nextFilters = { ...filters, [field]: value };

    if (field === "category") {
      nextFilters.subcategory = "";
      const validProducts = selectedProducts.filter((productName) => {
        const product = normalizedProducts.find(
          (item) => item.name === productName,
        );
        if (!product) return false;
        return !value || product.category === value;
      });
      setSelectedProducts(validProducts);
      nextFilters.products = getEffectiveProducts(nextFilters, validProducts);
    }

    if (field === "subcategory") {
      const validProducts = selectedProducts.filter((productName) => {
        const product = normalizedProducts.find(
          (item) => item.name === productName,
        );
        if (!product) return false;
        if (nextFilters.category && product.category !== nextFilters.category)
          return false;
        if (value && product.subcategory !== value) return false;
        return true;
      });
      setSelectedProducts(validProducts);
      nextFilters.products = getEffectiveProducts(nextFilters, validProducts);
    }

    emitFilters(nextFilters);
  };

  const toggleProduct = (productName) => {
    const exists = selectedProducts.includes(productName);
    const nextSelected = exists
      ? selectedProducts.filter((product) => product !== productName)
      : [...selectedProducts, productName];
    setSelectedProducts(nextSelected);
    const nextFilters = {
      ...filters,
      products: getEffectiveProducts(filters, nextSelected),
    };
    emitFilters(nextFilters);
  };

  const clearSelectedProducts = () => {
    setSelectedProducts([]);
    const nextFilters = {
      ...filters,
      products: getEffectiveProducts(filters, []),
    };
    emitFilters(nextFilters);
  };

  const toggleDmsProvider = (providerName) => {
    const current = Array.isArray(filters.dmsProviders)
      ? filters.dmsProviders
      : [];
    const exists = current.includes(providerName);
    const nextProviders = exists
      ? current.filter((provider) => provider !== providerName)
      : [...current, providerName];
    emitFilters({ ...filters, dmsProviders: nextProviders });
  };

  const handleClear = () => {
    const resetFilters = {
      status: "all",
      leadType: "all",
      leadSource: "all",
      serviceDashboard: "all",
      search: "",
      category: "",
      subcategory: "",
      dmsProviders: [],
      products: [],
      salesRep: "all",
    };
    setSelectedProducts([]);
    setProductMenuOpen(false);
    setDmsMenuOpen(false);
    emitFilters(resetFilters);
  };

  const selectedProductCount = selectedProducts.length;
  const selectedDmsCount = filters.dmsProviders.length;

  return (
    <div className="partner-lead-filters">
      {/* ==================================================
          FILTER ROW 1: Search + Status + Type + Source
          ================================================== */}
      <div className="filter-row">
        {/* Search */}
        <div className="partner-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            value={filters.search}
            placeholder="Search leads, dealerships, email..."
            onChange={(event) => handleChange("search", event.target.value)}
          />
          {filters.search && (
            <button
              type="button"
              className="partner-search-clear"
              onClick={() => handleChange("search", "")}
              aria-label="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Status */}
        <select
          className="filter-select"
          value={filters.status}
          onChange={(event) => handleChange("status", event.target.value)}
        >
          <option value="all">All Status</option>
          {statuses.map((status) => (
            <option key={status.id || status.name} value={status.name}>
              {status.name}
            </option>
          ))}
        </select>

        {/* Lead Type */}
        <select
          className="filter-select"
          value={filters.leadType}
          onChange={(event) => handleChange("leadType", event.target.value)}
        >
          <option value="all">All Types</option>
          {leadTypes.map((item) => (
            <option key={item.id || item.name} value={item.name}>
              {item.name}
            </option>
          ))}
        </select>

        {/* Lead Source */}
        <select
          className="filter-select"
          value={filters.leadSource}
          onChange={(event) => handleChange("leadSource", event.target.value)}
        >
          <option value="all">All Sources</option>
          {leadSources.map((item) => (
            <option key={item.id || item.name} value={item.name}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      {/* ==================================================
          FILTER ROW 2: Category + Subcategory + Products + Dashboard + DMS + Sales Rep + Clear
          ================================================== */}
      <div className="filter-row filter-row-2">
        {/* Category */}
        <select
          className="filter-select"
          value={filters.category}
          onChange={(event) => handleChange("category", event.target.value)}
          disabled={loadingProducts}
        >
          <option value="">All Categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        {/* Subcategory */}
        <select
          className="filter-select"
          value={filters.subcategory}
          onChange={(event) => handleChange("subcategory", event.target.value)}
          disabled={loadingProducts || !filters.category}
        >
          <option value="">All Subcategories</option>
          {subcategories.map((subcategory) => (
            <option key={subcategory} value={subcategory}>
              {subcategory}
            </option>
          ))}
        </select>

        {/* Products - Dropdown Toggle */}
        <div className="filter-dropdown-wrapper" ref={productDropdownRef}>
          <button
            type="button"
            className={`filter-dropdown-btn ${selectedProductCount > 0 ? "has-selection" : ""}`}
            onClick={() => {
              setProductMenuOpen(!productMenuOpen);
              setDmsMenuOpen(false);
            }}
            disabled={loadingProducts}
          >
            <span>
              <i className="fas fa-box-open"></i>
              {selectedProductCount > 0
                ? `${selectedProductCount} selected`
                : "Products"}
            </span>
            <i
              className={`fas fa-chevron-${productMenuOpen ? "up" : "down"}`}
            ></i>
          </button>

          {productMenuOpen && (
            <div className="filter-dropdown-menu product-menu">
              <div className="filter-dropdown-header">
                <span>Select Products</span>
                {selectedProductCount > 0 && (
                  <button type="button" onClick={clearSelectedProducts}>
                    Clear
                  </button>
                )}
              </div>
              <div className="filter-dropdown-body">
                {availableProducts.length === 0 ? (
                  <div className="filter-dropdown-empty">
                    No products available
                  </div>
                ) : (
                  availableProducts.map((product) => {
                    const checked = selectedProducts.includes(product.name);
                    return (
                      <label
                        key={product.id || product.name}
                        className="filter-checkbox-option"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProduct(product.name)}
                        />
                        <span>{product.name}</span>
                        {product.subcategory && (
                          <small>{product.subcategory}</small>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Service Dashboard */}
        <select
          className="filter-select"
          value={filters.serviceDashboard}
          onChange={(event) =>
            handleChange("serviceDashboard", event.target.value)
          }
        >
          <option value="all">All Dashboards</option>
          {serviceDashboards.map((item) => (
            <option key={item.id || item.name} value={item.name}>
              {item.name}
            </option>
          ))}
        </select>

        {/* DMS Provider - Dropdown Toggle */}
        <div className="filter-dropdown-wrapper" ref={dmsDropdownRef}>
          <button
            type="button"
            className={`filter-dropdown-btn ${selectedDmsCount > 0 ? "has-selection" : ""}`}
            onClick={() => {
              setDmsMenuOpen(!dmsMenuOpen);
              setProductMenuOpen(false);
            }}
          >
            <span>
              <i className="fas fa-database"></i>
              {selectedDmsCount > 0
                ? `${selectedDmsCount} selected`
                : "DMS Provider"}
            </span>
            <i className={`fas fa-chevron-${dmsMenuOpen ? "up" : "down"}`}></i>
          </button>

          {dmsMenuOpen && (
            <div className="filter-dropdown-menu dms-menu">
              <div className="filter-dropdown-header">
                <span>Select DMS Providers</span>
                {selectedDmsCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      emitFilters({ ...filters, dmsProviders: [] });
                      setDmsMenuOpen(false);
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="filter-dropdown-body">
                {dmsProviders.length === 0 ? (
                  <div className="filter-dropdown-empty">
                    No DMS providers available
                  </div>
                ) : (
                  dmsProviders.map((provider) => {
                    const providerName =
                      typeof provider === "string"
                        ? provider
                        : provider?.name ||
                          provider?.dms_name ||
                          provider?.provider_name;
                    if (!providerName) return null;
                    const checked = filters.dmsProviders.includes(providerName);
                    return (
                      <label
                        key={provider.id || provider.dms_id || providerName}
                        className="filter-checkbox-option"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDmsProvider(providerName)}
                        />
                        <span>{providerName}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sales Rep (Admin/SM only) */}
        {ownerCanFilterSalesRep && (
          <select
            className="filter-select"
            value={filters.salesRep}
            onChange={(event) => handleChange("salesRep", event.target.value)}
          >
            <option value="all">All Reps</option>
            {salesReps.map((salesRep) => {
              const id = salesRep?.user_id ?? salesRep?.id ?? salesRep?.userId;
              const name =
                salesRep?.full_name ??
                salesRep?.fullName ??
                salesRep?.name ??
                salesRep?.email ??
                "Sales Rep";
              if (id === undefined) return null;
              return (
                <option key={id} value={id}>
                  {name}
                </option>
              );
            })}
          </select>
        )}

        {/* Clear Button */}
        <button
          type="button"
          className="btn btn-secondary btn-sm filter-clear-btn"
          onClick={handleClear}
        >
          <i className="fas fa-undo"></i> Clear
        </button>
      </div>
    </div>
  );
}

export default LeadFilters;
